// Helper per aprire la navigazione esterna dal pulsante "Naviga".
// Strategia (CLAUDE.md §3):
// - Le preferenze (una per mobile, una per desktop) sono salvate sul server
//   sull'account unico, quindi valgono da QUALSIASI dispositivo.
// - Per restare sincrono al click, openNavigation legge una cache in memoria
//   popolata all'avvio da loadNavPreferences() e aggiornata da saveNavPreferences().
// - Mobile in modalità "auto" tenta Waze e ripiega sul navigatore di sistema.
// - Desktop apre sempre in una nuova scheda.

import { getSettings, updateSettings, type Settings } from '../api/settings';
import type { NavAppMobile, NavAppDesktop } from '@camper-diary/shared';

export type { NavAppMobile, NavAppDesktop };

export type Device = 'ios' | 'android' | 'desktop';

let cache: Settings = { navAppMobile: 'auto', navAppDesktop: 'google' };

export function detectDevice(): Device {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** Cache corrente delle preferenze (sincrona). */
export function getNavPreferences(): Settings {
  return cache;
}

/** Carica le preferenze dal server nella cache. Da chiamare all'avvio. */
export async function loadNavPreferences(): Promise<Settings> {
  cache = await getSettings();
  return cache;
}

/** Salva le preferenze sul server e aggiorna la cache locale. */
export async function saveNavPreferences(patch: Partial<Settings>): Promise<Settings> {
  cache = await updateSettings(patch);
  return cache;
}

function openDesktopNavigation(lat: number, lon: number): void {
  let url: string;
  switch (cache.navAppDesktop) {
    case 'waze':
      url = `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`;
      break;
    case 'osm':
      url = `https://www.openstreetmap.org/directions?to=${lat}%2C${lon}`;
      break;
    case 'google':
    default:
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${lat},${lon}`,
      )}`;
      break;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function openNavigation(lat: number, lon: number, label?: string): void {
  const device = detectDevice();

  if (device === 'desktop') {
    openDesktopNavigation(lat, lon);
    return;
  }

  switch (cache.navAppMobile) {
    case 'waze':
      // Universal link: apre l'app se installata, altrimenti la web app.
      window.location.href = `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`;
      return;
    case 'google':
      window.location.href = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
      return;
    case 'apple':
      window.location.href = `maps://?daddr=${lat},${lon}${
        label ? `&q=${encodeURIComponent(label)}` : ''
      }`;
      return;
    case 'system':
      window.location.href = `geo:${lat},${lon}?q=${lat},${lon}${
        label ? `(${encodeURIComponent(label)})` : ''
      }`;
      return;
    case 'auto':
    default:
      break;
  }

  // Modalità automatica: tenta Waze, fallback sul provider di sistema.
  const waze = `waze://?ll=${lat},${lon}&navigate=yes`;
  const fallback =
    device === 'ios'
      ? `maps://?daddr=${lat},${lon}${label ? `&q=${encodeURIComponent(label)}` : ''}`
      : `geo:${lat},${lon}?q=${lat},${lon}${label ? `(${encodeURIComponent(label)})` : ''}`;

  // Tenta Waze; se dopo 1.5s siamo ancora visibili (= Waze non ha aperto),
  // ripiega sul provider di sistema.
  const start = Date.now();
  window.location.href = waze;

  setTimeout(() => {
    if (Date.now() - start < 2000 && document.visibilityState === 'visible') {
      window.location.href = fallback;
    }
  }, 1500);
}
